import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type DragEvent,
} from 'react';
import { Info, Loader2, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { embedLayout } from '@/lib/embed-layout';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ALLOWED_IMAGE_TYPES, ALLOWED_UPLOAD_TYPES } from '@/config/constants';
import {
  CUSTOM_PLACEMENT,
  embroideryFieldContext,
  embroideryMaxRelWidth,
  presetMaxRelWidthForHeight,
  resolveGuidedPlacement,
  useGuidedEntryStore,
  type GuidedEmbroidery,
  type GuidedSide,
  type GuidedSideState,
} from '@/stores/guidedEntryStore';
import { useEmbroideryStore } from '@/stores/embroideryStore';
import {
  MIN_EMBROIDERY_WIDTH_MM,
  SYSTEM_LAYER_NAME_SET,
  countThreads,
  type PrintContext,
  type RelativeDesignState,
} from '@designdeck/shared';
import { useDesignStore } from '@/stores/designStore';
import {
  guidedSizeInfo,
  relWidthForDisplaySize,
  type GuidedPrintGeometry,
} from './guidedSize';
import {
  GuidedPlacementPicker,
  type PlacementOption,
} from './GuidedPlacementPicker';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import { CanvasEditor } from '@/components/editor/CanvasEditor';
import { useSizeIndicator, type SizeInfo } from '@/hooks/useSizeIndicator';
import {
  GUIDED_FRAME_CONTROL_SIZE,
  GUIDED_FRAME_MIN_SCALE,
  guidedCanvasConfig,
  guidedFramePadding,
} from './guidedCanvasConfig';
import { EmbroideryControls } from './EmbroideryControls';
import { useGuidedUpload } from './useGuidedUpload';

/**
 * One side of the guided entry: upload a motif, place it, size it.
 *
 * Layout follows the source design — a paper-2 card with an eyebrow header, a
 * dashed drop zone while empty, and a preview frame beside the controls once
 * filled.
 *
 * In an embroidery run the card is the seller's whole path to an artwork: the
 * upload is analysed on the server, the threads are chosen in the modal, and
 * confirming renders the stitch optics in print resolution — that render is
 * the motif from then on, and the production file.
 */

export interface GuidedEmbroideryLimits {
  /** Per-design thread ceiling of the reference position. */
  maxColors: number;
  /** DecorationMethod.minDpi of the reference position; sizes the render. */
  minDpi: number | null;
}

interface GuidedMotifCardProps {
  side: GuidedSide;
  title: string;
  subtitle: string;
  emptyHint: string;
  dropTitle: string;
  placements: PlacementOption[];
  /** Reference print area: placements are fitted and sized against it. */
  printContext: PrintContext;
  /**
   * Opens the canvas editor for free placement. Absent until the custom round
   * trip exists — the picker then offers presets only, rather than a tile that
   * leads nowhere.
   */
  onCustomPlacement?: () => void;
  /**
   * Present in an embroidery run: the card analyses the upload, maps threads
   * and renders the artwork instead of placing a print motif.
   */
  embroidery?: GuidedEmbroideryLimits;
}

export function GuidedMotifCard({
  side,
  title,
  subtitle,
  emptyHint,
  dropTitle,
  placements,
  printContext,
  onCustomPlacement,
  embroidery,
}: GuidedMotifCardProps) {
  const { t } = useTranslation();
  const compact = embedLayout().foldSecondary;
  const state = useGuidedEntryStore((s) => s[side]);
  const clearMotif = useGuidedEntryStore((s) => s.clearMotif);
  const setPlacement = useGuidedEntryStore((s) => s.setPlacement);
  const setRelWidthOverride = useGuidedEntryStore((s) => s.setRelWidthOverride);

  const [isDragOver, setIsDragOver] = useState(false);
  const { isUploading, error, setError, handleFile } = useGuidedUpload({
    side,
    embroidery,
  });

  const isEmbroideryRun = embroidery !== undefined;

  const onDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!isUploading) void handleFile(e.dataTransfer.files?.[0]);
  };

  const onSelect = (e: ChangeEvent<HTMLInputElement>) => {
    void handleFile(e.target.files?.[0]);
    e.target.value = '';
  };

  // A custom side is owned by the canvas, so its geometry is read back from
  // there rather than derived — that is also what keeps the size and quality
  // readout honest after the seller scaled the motif in the editor.
  const editingSide = useGuidedEntryStore((s) => s.editingSide);
  // canvasStateManager holds a single canvas, so only the side being placed
  // may mount an editor.
  const isEditing = editingSide === side;
  const { sizeInfo } = useSizeIndicator();
  const canvasPlacement = useCanvasStateManager(
    (s) => s.masterPositions[side]?.relativeState ?? null
  );
  const placement =
    state.placement === CUSTOM_PLACEMENT
      ? canvasPlacement
      : resolveGuidedPlacement(state, printContext);

  // In an embroidery run the frame shows the embroidery field (the Print
  // Area cut to the machine's 9 × 9 cm), so a 9 cm motif fills it. Canvas
  // state stays relative to the Print Area; the field is a view onto it.
  const frameContext = useMemo(
    () =>
      isEmbroideryRun ? embroideryFieldContext(printContext) : printContext,
    [isEmbroideryRun, printContext]
  );
  const areaToFrame = printContext.widthPx / frameContext.widthPx;
  const toFrame = (
    p: RelativeDesignState | null
  ): RelativeDesignState | null =>
    p && isEmbroideryRun
      ? {
          ...p,
          relCenterX: 0.5,
          relCenterY: 0.5,
          relWidth: p.relWidth * areaToFrame,
        }
      : p;
  const shownPlacement = toFrame(placement);
  const geometry: GuidedPrintGeometry = {
    widthPx: frameContext.widthPx,
    pxPerMm: frameContext.pxPerMm,
  };

  const pendingEmbroidery = state.embroidery && !state.motif;
  const confirmedThreads = state.embroidery?.mapping
    ? countThreads(state.embroidery.mapping)
    : 0;

  const stateLabel = isUploading
    ? isEmbroideryRun
      ? t('creator.guidedMotif.analysing')
      : t('creator.guidedMotif.uploading')
    : pendingEmbroidery
      ? t('creator.guidedMotif.threadsOpen')
      : state.motif
        ? isEmbroideryRun
          ? t('creator.guidedMotif.threadCount', { count: confirmedThreads })
          : t('creator.guidedMotif.ready')
        : t('creator.guidedMotif.open');

  /** Tapping the motif in a still frame takes that side into the editor. */
  const activateSide = () => {
    if (isEditing || isEmbroideryRun) return;
    setPlacement(side, CUSTOM_PLACEMENT);
    onCustomPlacement?.();
  };

  /**
   * Width change from the size field or the slider.
   *
   * A preset side stores an override and re-fits. The side in the editor
   * scales its canvas object instead, through the same object:modified the
   * corner handles fire — so history, size readout and autosave all follow.
   */
  const resizeSide = (relWidth: number | null) => {
    if (state.placement !== CUSTOM_PLACEMENT) {
      // The control speaks in frame widths; the store keeps Print Area widths.
      setRelWidthOverride(
        side,
        relWidth === null ? null : relWidth / areaToFrame
      );
      return;
    }
    if (!isEditing || relWidth === null) return;
    const canvas = useDesignStore.getState().canvas;
    if (!canvas) return;
    const object = canvas
      .getObjects()
      .find((o) => !SYSTEM_LAYER_NAME_SET.has(o.name ?? ''));
    if (!object || !(object.width > 0)) return;
    // relWidth is relative to the print area — not to the canvas, which
    // carries the seam for the selection handles on top of it.
    const scale = (relWidth * printContext.widthPx) / object.width;
    object.set({ scaleX: scale, scaleY: scale });
    object.setCoords();
    canvas.fire('object:modified', { target: object });
    canvas.requestRenderAll();
  };

  // The embroidery field bounds the motif on both axes; a print preset is
  // capped where its height would overshoot the area around its anchor.
  const maxRelWidth =
    isEmbroideryRun && (state.motif ?? state.embroidery)
      ? embroideryMaxRelWidth(
          (state.motif ?? state.embroidery) as {
            width: number;
            height: number;
          },
          frameContext
        )
      : presetMaxRelWidthForHeight(state, printContext) * areaToFrame;

  const shownFile = state.motif ?? state.embroidery;

  return (
    // A size container: the filled card lays itself out by its own width,
    // not the viewport's — inside an iframe the two differ widely.
    <div className="dc-panel flex min-w-0 flex-col gap-3.5 rounded-card bg-[var(--paper-2)] p-5 [container-type:inline-size]">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-baseline gap-2.5">
          <span className="dc-type-section text-[var(--ink)]">{title}</span>
          <span className="text-xs text-[var(--mute)]">{subtitle}</span>
        </div>
        <span
          className={cn(
            'dc-type-data whitespace-nowrap',
            state.motif
              ? 'text-[var(--cobalt-500)]'
              : pendingEmbroidery
                ? 'text-[var(--primary,var(--cobalt-500))]'
                : 'text-[var(--mute)]'
          )}
        >
          {stateLabel}
        </span>
      </div>

      {!shownFile ? (
        <>
          <label
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={onDrop}
            className={cn(
              'flex min-h-[220px] cursor-pointer flex-col items-center justify-center gap-2.5 rounded-control border-2 border-dashed p-6',
              'transition-[background,border-color] duration-[120ms]',
              isDragOver
                ? 'border-[var(--cobalt-500)] bg-[var(--cobalt-100)]'
                : 'border-[var(--cobalt-300)] bg-[var(--paper)]'
            )}
          >
            <input
              type="file"
              accept={(isEmbroideryRun
                ? ALLOWED_IMAGE_TYPES
                : ALLOWED_UPLOAD_TYPES
              ).join(',')}
              onChange={onSelect}
              disabled={isUploading}
              className="hidden"
            />
            <span className="grid h-[46px] w-[46px] place-items-center rounded-full bg-[var(--cobalt-100)] text-[var(--cobalt-500)]">
              {isUploading ? (
                // The editor's own upload spinner (Loader2 + animate-spin),
                // so the round trip reads the same on both surfaces.
                <Loader2
                  role="status"
                  aria-label={
                    isEmbroideryRun
                      ? t('creator.guidedMotif.detectingColors')
                      : t('creator.guidedMotif.uploadingMotif')
                  }
                  className="h-5 w-5 animate-spin"
                />
              ) : (
                <Plus className="h-5 w-5" />
              )}
            </span>
            <span className="dc-type-card-title text-[var(--ink)]">
              {isUploading
                ? isEmbroideryRun
                  ? t('creator.guidedMotif.detectingColorsProgress')
                  : t('creator.guidedMotif.uploadingProgress')
                : dropTitle}
            </span>
            <span className="-mt-1 text-[12.5px] text-[var(--mute)]">
              {isEmbroideryRun
                ? t('creator.guidedMotif.dropHintEmbroidery')
                : t('creator.guidedMotif.dropHint')}
            </span>
          </label>
          <p className="text-[12.5px] leading-[1.45] text-[var(--mute)]">
            {emptyHint}
          </p>
        </>
      ) : (
        // Frame beside the controls from a 600 px card on (560 px inside its
        // padding); a narrower card puts the frame on top.
        <div className="dc-inset dc-split grid grid-cols-1 items-start justify-items-center gap-[18px] rounded-control bg-[var(--paper)] p-3.5 [@container(min-width:560px)]:grid-cols-[auto_minmax(0,1fr)]">
          <MotifFrame
            state={state}
            placement={
              shownPlacement ??
              // Before the threads are confirmed there is no motif yet; the
              // frame previews the analysed upload where the artwork will sit.
              (state.embroidery
                ? toFrame(
                    resolveGuidedPlacement(
                      { ...state, motif: pendingAsMotif(state.embroidery) },
                      printContext
                    )
                  )
                : null)
            }
            imageUrl={state.motif?.url ?? state.embroidery?.previewUrl ?? ''}
            imageSize={shownFile}
            printContext={frameContext}
            isEditing={isEditing}
            isEmbroidery={isEmbroideryRun}
            onActivate={activateSide}
          />

          <div className="flex w-full min-w-0 flex-col gap-3.5">
            <div className="flex items-center gap-3">
              {/* Inside the Shopify admin the frame already shows the motif;
                  the row keeps only its pixel size and the remove action. */}
              {compact ? (
                <span
                  title={shownFile.fileName}
                  className="flex-1 text-xs text-[var(--mute)]"
                >
                  {shownFile.width} × {shownFile.height} px
                </span>
              ) : (
                <>
                  <span
                    className="box-border h-[44px] w-[44px] flex-none rounded-control bg-[var(--paper-2)] bg-contain bg-center bg-no-repeat p-[5px]"
                    style={{
                      backgroundImage: `url("${state.motif?.url ?? state.embroidery?.previewUrl ?? ''}")`,
                      backgroundOrigin: 'content-box',
                    }}
                  />
                  <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                    <span className="dc-type-card-title truncate">
                      {shownFile.fileName}
                    </span>
                    <span className="dc-type-data text-[var(--mute)]">
                      {shownFile.width} × {shownFile.height} px
                    </span>
                  </span>
                </>
              )}
              <button
                type="button"
                title={t('creator.guidedMotif.remove')}
                onClick={() => {
                  clearMotif(side);
                  if (isEmbroideryRun) {
                    useEmbroideryStore.getState().clearEmbroidery();
                  }
                }}
                className="grid h-[34px] w-[34px] flex-none cursor-pointer place-items-center rounded-control text-[var(--ink-2)] hover:bg-[var(--paper-2)]"
              >
                <Trash2 className="h-[15px] w-[15px]" />
              </button>
            </div>

            {isEmbroideryRun && state.embroidery ? (
              <EmbroideryControls
                side={side}
                embroidery={state.embroidery}
                limits={embroidery}
                printContext={printContext}
                onError={setError}
              />
            ) : (
              <GuidedPlacementPicker
                label={t('creator.guidedMotif.placementLabel')}
                options={placements}
                value={state.placement}
                onChange={(placement) => {
                  setPlacement(side, placement);
                  if (placement === CUSTOM_PLACEMENT) onCustomPlacement?.();
                }}
              />
            )}

            {state.motif && (
              <SizeControl
                placement={shownPlacement}
                geometry={geometry}
                motifWidthPx={state.motif.width}
                maxRelWidth={maxRelWidth}
                minWidthCm={isEmbroideryRun ? MIN_EMBROIDERY_WIDTH_MM / 10 : 1}
                // A custom side that is not in the editor has nothing to scale;
                // tapping its motif brings it in.
                disabled={state.placement === CUSTOM_PLACEMENT && !isEditing}
                fromFrame={state.placement === CUSTOM_PLACEMENT}
                inEmbroideryField={isEmbroideryRun}
                liveSizeInfo={isEditing ? sizeInfo : null}
                onResize={resizeSide}
              />
            )}
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="text-[12.5px] text-[var(--danger)]">
          {error}
        </p>
      )}
    </div>
  );
}

/** The analysed upload, shaped like a motif so the frame can preview it. */
function pendingAsMotif(embroidery: GuidedEmbroidery) {
  return {
    assetId: '',
    url: embroidery.previewUrl,
    width: embroidery.width,
    height: embroidery.height,
    fileName: embroidery.fileName,
  };
}

/**
 * The print-area frame.
 *
 * While the seller is placing freely, this is the real editor with the garment
 * layer left out: dashed print-area border, the motif, and the move / corner
 * scale / rotate / delete controls CanvasEditor installs anyway. Nothing about
 * the interaction is reimplemented here.
 *
 * For a preset the frame is a still image — there is nothing to manipulate, and
 * mounting a canvas per card would cost a Fabric instance for no gain. In an
 * embroidery run the frame is the embroidery field and stays still: the motif
 * is centred there and sized, never placed freely.
 */
function MotifFrame({
  state,
  placement,
  imageUrl,
  imageSize,
  printContext,
  isEditing,
  isEmbroidery,
  onActivate,
}: {
  state: GuidedSideState;
  placement: RelativeDesignState | null;
  imageUrl: string;
  imageSize: { width: number; height: number };
  printContext: PrintContext;
  isEditing: boolean;
  isEmbroidery: boolean;
  /** Called when the seller taps the motif in a still frame. */
  onActivate: () => void;
}) {
  const { t } = useTranslation();
  const config = useMemo(
    () => guidedCanvasConfig(printContext),
    [printContext]
  );
  const seam = guidedFramePadding(printContext);

  // The editor never saves on unmount, and this frame is unmounted the moment
  // the other side starts placing — so every change is flushed into
  // masterPositions here, the way DirectSaleDesignStep already does. Deferred
  // one macrotask: object:added fires before the editor finished configuring
  // the object, and the delay folds a drag's burst of events into one save.
  // An early save during canvas construction serializes no user objects and
  // is refused by the store's empty-canvas guard, so nothing is overwritten.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The editor mounts empty and restores the motif a few frames later — once
  // Fabric is up and the position is loaded. Until then the still stays put
  // underneath, so the frame never blinks blank between preset and editor;
  // and the restored motif is selected right away, so the handles are there
  // to grab without a first click. The editor registers onCanvasChange once
  // on mount, hence the ref rather than state inside the handler.
  const [editorHasMotif, setEditorHasMotif] = useState(false);
  const editorHasMotifRef = useRef(false);
  useEffect(() => {
    if (isEditing) return;
    editorHasMotifRef.current = false;
    setEditorHasMotif(false);
  }, [isEditing]);
  const handleCanvasChange = () => {
    if (!editorHasMotifRef.current) {
      const canvas = useDesignStore.getState().canvas;
      const motif = canvas
        ?.getObjects()
        .find((o) => !SYSTEM_LAYER_NAME_SET.has(o.name ?? ''));
      if (canvas && motif) {
        editorHasMotifRef.current = true;
        setEditorHasMotif(true);
        // object:added fires mid-restore; select once the load has settled.
        setTimeout(() => {
          if (
            useDesignStore.getState().canvas !== canvas ||
            !canvas.getObjects().includes(motif) ||
            canvas.getActiveObject() === motif
          )
            return;
          canvas.setActiveObject(motif);
          canvas.requestRenderAll();
        }, 0);
      }
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        useCanvasStateManager.getState().saveCurrentPosition();
      } catch {
        // Canvas not ready for serialization; the next change retries.
      }
    }, 50);
  };
  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    []
  );

  const stillStyle = placement
    ? {
        backgroundImage: `url("${imageUrl}")`,
        left: `${placement.relCenterX * 100}%`,
        top: `${placement.relCenterY * 100}%`,
        width: `${placement.relWidth * 100}%`,
        transform: 'translate(-50%, -50%)',
        aspectRatio: `${imageSize.width} / ${imageSize.height}`,
      }
    : undefined;

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={cn(
          'dc-print-frame relative w-[280px] bg-[var(--paper-2)]',
          // The still preview is clipped to the print area; the editor's
          // seam must overhang so the handles around the motif stay visible.
          isEditing ? 'z-10 overflow-visible' : 'overflow-hidden'
        )}
        style={
          {
            aspectRatio: `${printContext.widthPx} / ${printContext.heightPx}`,
            // Read by embedding themes that bound the frame by height as
            // well (the dc-print-frame role).
            '--dc-print-frame-ratio':
              printContext.widthPx / printContext.heightPx,
          } as CSSProperties
        }
      >
        {isEditing && !editorHasMotif && stillStyle ? (
          /* Bridges the editor's start-up: same still as before the tap,
             replaced the moment the motif lands on the canvas. */
          <div
            aria-hidden="true"
            className="absolute bg-contain bg-center bg-no-repeat"
            style={stillStyle}
          />
        ) : null}
        {isEditing ? (
          /* The canvas is the print area plus a seam on every side. The box
             stays the print area, so the canvas is laid out larger than it
             and shifted back by the seam — in percentages of the box, so it
             holds at any scale. */
          <div
            className="absolute"
            style={{
              left: `-${(seam / printContext.widthPx) * 100}%`,
              top: `-${(seam / printContext.heightPx) * 100}%`,
              width: `${(1 + (2 * seam) / printContext.widthPx) * 100}%`,
              height: `${(1 + (2 * seam) / printContext.heightPx) * 100}%`,
            }}
          >
            <CanvasEditor
              config={config}
              className="h-full w-full"
              containerPadding={0}
              controlScreenSize={GUIDED_FRAME_CONTROL_SIZE}
              minScaleFactor={GUIDED_FRAME_MIN_SCALE}
              onCanvasChange={handleCanvasChange}
            />
          </div>
        ) : stillStyle && isEmbroidery ? (
          <div
            role="img"
            aria-label={t('creator.guidedMotif.embroideryInField')}
            className="absolute bg-contain bg-center bg-no-repeat"
            style={stillStyle}
          />
        ) : stillStyle && state.motif ? (
          <button
            type="button"
            onClick={onActivate}
            aria-label={t('creator.guidedMotif.placeFreely')}
            title={t('creator.guidedMotif.clickToEdit')}
            className="absolute cursor-pointer border-0 bg-contain bg-center bg-no-repeat p-0 hover:outline hover:outline-2 hover:outline-[var(--cobalt-300)]"
            style={stillStyle}
          />
        ) : null}
        {/* The same dashed frame in both states — CSS, so it never depends on
            the canvas scale. Pointer events pass through to the handles. */}
        <div className="pointer-events-none absolute inset-0 border-2 border-dashed border-[var(--cobalt-300)]" />
      </div>
      <span className="dc-type-caption text-[var(--mute)]">
        {isEditing
          ? t('creator.guidedMotif.freePlacement')
          : isEmbroidery
            ? t(
                state.motif
                  ? 'creator.guidedMotif.embroideryFieldStitched'
                  : 'creator.guidedMotif.embroideryField',
                {
                  width: Math.round(printContext.widthMm / 10),
                  height: Math.round(printContext.heightMm / 10),
                }
              )
            : t('creator.guidedMotif.printArea')}
      </span>
    </div>
  );
}

interface SizeControlProps {
  /** Placement the size is read from — the geometry is the truth. */
  placement: RelativeDesignState | null;
  geometry: GuidedPrintGeometry;
  motifWidthPx: number;
  /** Ceiling as a fraction of the area width; 1 unless the field bounds it. */
  maxRelWidth?: number;
  /** Smallest width the slider and the field accept, in display units. */
  minWidthCm?: number;
  /** No way to apply a size right now — the field and slider are inert. */
  disabled?: boolean;
  /** The placement lives in the frame canvas rather than in a preset. */
  fromFrame?: boolean;
  /** The motif sits in an embroidery field, not a print area. */
  inEmbroideryField?: boolean;
  /**
   * Live size of the selected canvas object, when there is one.
   *
   * The same value the classic editor's bar shows, so the number keeps up with
   * a drag or a resize instead of only refreshing when the placement is stored.
   */
  liveSizeInfo?: SizeInfo | null;
  onResize: (relWidth: number | null) => void;
}

/**
 * Physical size of the placement.
 *
 * Reads like the Creator's image bar: the number is derived from the geometry
 * via pxPerMm, and typing one converts back through the same ratio. The quality
 * label comes from the same DPI thresholds the canvas editor uses.
 */
function SizeControl({
  placement,
  geometry,
  motifWidthPx,
  maxRelWidth = 1,
  minWidthCm = 1,
  disabled = false,
  fromFrame = false,
  inEmbroideryField = false,
  liveSizeInfo = null,
  onResize,
}: SizeControlProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string | null>(null);
  const derived = placement
    ? guidedSizeInfo(placement, geometry, motifWidthPx)
    : null;

  // Prefer the live value while the seller is manipulating the motif; fall back
  // to the stored placement so the size never disappears between selections.
  const info: SizeInfo | null = liveSizeInfo ?? derived;
  const dpiTitle =
    liveSizeInfo === null && derived
      ? `${Math.round(derived.dpi)} dpi`
      : undefined;

  if (!info) return null;

  const shown = draft ?? info.width.toFixed(1);

  // Slider range in display units: from a 1 cm logo up to the full print
  // area width — or as far as the embroidery field allows — the same bound
  // the typed value is capped at.
  const areaWidthCm = geometry.widthPx / geometry.pxPerMm / 10;
  const cap = Math.min(1, Math.max(maxRelWidth, 0));
  const sliderMax = Math.max(
    minWidthCm + 0.1,
    Math.round(areaWidthCm * cap * 10) / 10
  );
  const sliderMin = Math.min(minWidthCm, sliderMax - 0.1);
  const floor = Math.min(
    cap,
    (sliderMin * 10 * geometry.pxPerMm) / geometry.widthPx
  );
  const clamp = (relWidth: number) => Math.min(Math.max(relWidth, floor), cap);

  const commit = () => {
    if (draft === null) return;
    const parsed = Number.parseFloat(draft.replace(',', '.'));
    const relWidth = relWidthForDisplaySize(parsed, geometry);
    // A cleared or nonsensical entry falls back to the placement's own size
    // rather than collapsing the design.
    onResize(relWidth === null ? null : clamp(relWidth));
    setDraft(null);
  };

  const help = inEmbroideryField
    ? t('creator.guidedMotif.embroiderySizeHelp', {
        min: sliderMin.toFixed(1),
        max: sliderMax.toFixed(1),
        unit: info.unit,
      })
    : fromFrame
      ? t('creator.guidedMotif.frameSizeHelp')
      : t('creator.guidedMotif.presetSizeHelp');
  // Inside the Shopify admin the explanation sits behind an info icon, which
  // keeps the step within the admin frame's height.
  const { foldSecondary, helpAsTooltip } = embedLayout();
  // Beside the slider in the reference design; in the narrower admin column it
  // gets a line of its own below.
  const quality = info.qualityLabel ? (
    <span
      className="dc-type-label"
      style={{ color: info.quality?.color }}
      // The live value carries no DPI, so the tooltip only appears for
      // the derived one rather than showing a made-up number.
      title={dpiTitle}
    >
      {info.qualityLabel}
    </span>
  ) : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1">
          <span className="dc-type-label text-[var(--mute)]">
            {t('creator.guidedMotif.size')}
          </span>
          {helpAsTooltip && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={t('creator.guidedMotif.sizeInfo')}
                    className="grid h-5 w-5 cursor-help place-items-center rounded-full text-[var(--mute)] hover:text-[var(--ink)]"
                  >
                    <Info className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[260px] leading-[1.45]">
                  {help}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
        <span className="dc-type-data text-[var(--ink)]">
          {info.displaySize}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span className="inline-flex h-[34px] items-center gap-1.5 rounded-control bg-[var(--paper-2)] px-2.5">
          <input
            type="number"
            min={0.1}
            step={0.1}
            value={shown}
            disabled={disabled}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            className="dc-type-data w-14 border-none bg-transparent text-right outline-none"
            aria-label={t('creator.guidedMotif.widthIn', { unit: info.unit })}
          />
          <span className="dc-type-data text-[var(--mute)]">{info.unit}</span>
        </span>
        <input
          type="range"
          min={sliderMin}
          max={sliderMax}
          step={0.1}
          value={Math.min(Math.max(info.width, sliderMin), sliderMax)}
          disabled={disabled}
          onChange={(e) => {
            const relWidth = relWidthForDisplaySize(
              Number.parseFloat(e.target.value),
              geometry
            );
            if (relWidth !== null) onResize(clamp(relWidth));
          }}
          aria-label={t('creator.guidedMotif.adjustWidth', { unit: info.unit })}
          className="h-1 min-w-[80px] flex-1 cursor-pointer accent-[var(--cobalt-500)] disabled:cursor-default disabled:opacity-40"
        />
        {foldSecondary ? null : quality}
      </div>
      {foldSecondary ? quality : null}
      {helpAsTooltip ? null : (
        <p className="text-xs leading-[1.45] text-[var(--mute)]">{help}</p>
      )}
    </div>
  );
}
